import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTasks } from "@/hooks/use-tasks";
import { CheckSquare, ChevronDown, ChevronRight, Plus, Loader2, CalendarClock, Edit2, Trash2, User } from "lucide-react";
import { format, isPast, isToday } from "date-fns";
import { UpdateTaskModal } from "./modals/UpdateTaskModal";
import { CreateTaskModal } from "./modals/CreateTaskModal";
import { ClaimTask } from "@/api/generated/client/models";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";

interface TasksModuleProps {
    claimId: number;
    isOpen: boolean;
    onToggle: () => void;
    canWrite: boolean;
}

export function TasksModule({ claimId, isOpen, onToggle, canWrite }: TasksModuleProps) {
    const { tasks, isLoading, updateTask, deleteTaskAsync } = useTasks(claimId);
    const [selectedTask, setSelectedTask] = useState<ClaimTask | null>(null);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState<ClaimTask | null>(null);

    // Tasks are now sorted by the hook

    return (
        <Card className="shadow-xs w-full max-w-full overflow-hidden flex-shrink-0">
            <Collapsible open={isOpen} onOpenChange={onToggle}>
                <CollapsibleTrigger className="w-full">
                    <CardHeader className="py-3 px-4 hover:bg-muted/50 cursor-pointer transition-colors overflow-hidden w-full">
                        <div className="flex items-center justify-between min-w-0 w-full gap-2">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                <CheckSquare className="h-4 w-4 shrink-0" />
                                <CardTitle className="text-sm font-medium truncate">Tasks</CardTitle>
                                <Badge variant="secondary" className="text-xs h-5 px-1.5 min-w-[1.25rem] shrink-0 flex items-center justify-center">
                                    {tasks?.filter(t => t.status !== 'completed').length || 0}
                                </Badge>
                            </div>
                            {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                        </div>
                    </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <CardContent className="px-4 pb-4 pt-0 w-full overflow-hidden">
                        <div className="pb-2 border-b mb-1"></div>
                        {isLoading ? (
                            <div className="flex justify-center p-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
                        ) : tasks?.length === 0 ? (
                            <div className="text-center py-6 text-sm text-muted-foreground">
                                No tasks yet
                            </div>
                        ) : (
                            <div className="space-y-2 pt-2 max-h-[300px] overflow-y-auto overflow-x-hidden pr-1 scrollbar-thin scrollbar-thumb-muted-foreground/20 hover:scrollbar-thumb-muted-foreground/40 w-full max-w-full">
                                {tasks.map((task) => (
                                    <div
                                        key={task.id}
                                        className={`flex items-start gap-2 p-2 hover:bg-muted/50 rounded-md group cursor-pointer w-full min-w-0 overflow-hidden ${task.status === 'completed' ? 'opacity-60' : ''}`}
                                        onClick={() => {
                                            if (!canWrite) return;
                                            updateTask({ taskId: task.id, updates: { status: task.status === 'completed' ? 'pending' : 'completed' } });
                                        }}
                                    >
                                        <Checkbox
                                            checked={task.status === 'completed'}
                                            onCheckedChange={(checked) => {
                                                if (!canWrite) return;
                                                updateTask({ taskId: task.id, updates: { status: checked ? 'completed' : 'pending' } });
                                            }}
                                            disabled={!canWrite}
                                            onClick={(e) => e.stopPropagation()}
                                            className="shrink-0 mt-0.5"
                                        />
                                        <div className="flex-1 w-0 min-w-0">
                                            <p className={`text-xs font-medium leading-tight whitespace-normal break-words ${task.status === 'completed' ? "line-through text-muted-foreground" : ""}`}>
                                                {task.title}
                                            </p>
                                            <div className="flex flex-wrap gap-1 mt-1 w-full max-w-full overflow-hidden">
                                                {task.priority && task.status !== 'completed' && (
                                                    <Badge variant="outline" className={`text-[10px] h-4 px-1 shrink-0 ${task.priority === 'urgent' ? 'text-red-500 border-red-200 bg-red-50' :
                                                        task.priority === 'high' ? 'text-orange-500 border-orange-200 bg-orange-50' :
                                                            'text-muted-foreground'
                                                        }`}>
                                                        {task.priority}
                                                    </Badge>
                                                )}
                                                {task.dueDate && task.status !== 'completed' && (
                                                    <Badge variant="outline" className={`text-[10px] h-4 px-1 gap-1 shrink-0 ${isPast(new Date(task.dueDate)) && !isToday(new Date(task.dueDate))
                                                        ? 'text-red-600 border-red-200 bg-red-50'
                                                        : 'text-muted-foreground border-muted-foreground/30'
                                                        }`}>
                                                        <CalendarClock className="h-3 w-3" />
                                                        {format(new Date(task.dueDate), "MMM d")}
                                                    </Badge>
                                                )}
                                                {task.assignedToName && task.status !== 'completed' && (
                                                    <Badge variant="outline" className="text-[10px] h-4 px-1 gap-1 shrink-0 text-blue-600 border-blue-200 bg-blue-50">
                                                        <User className="h-3 w-3" />
                                                        {task.assignedToName}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 text-muted-foreground hover:text-foreground shrink-0"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!canWrite) return;
                                                    setSelectedTask(task);
                                                    setIsUpdateModalOpen(true);
                                                }}
                                                disabled={!canWrite}
                                            >
                                                <Edit2 className="h-3 w-3" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 text-muted-foreground hover:text-destructive shrink-0"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!canWrite) return;
                                                    setTaskToDelete(task);
                                                }}
                                                disabled={!canWrite}
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="pt-3 border-t mt-3 w-full">
                            <Button size="sm" className="w-full h-8 text-xs" onClick={(e) => { e.stopPropagation(); if (canWrite) setIsCreateModalOpen(true); }} disabled={!canWrite}>
                                <Plus className="h-3 w-3 mr-2" /> Add Task
                            </Button>
                        </div>
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>
            <UpdateTaskModal
                claimId={claimId}
                task={selectedTask}
                open={isUpdateModalOpen}
                onOpenChange={setIsUpdateModalOpen}
            />
            <CreateTaskModal claimId={claimId} open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen} />
            <ConfirmDeleteDialog
                open={taskToDelete !== null}
                onOpenChange={(open) => { if (!open) setTaskToDelete(null); }}
                onConfirm={async () => { if (taskToDelete) await deleteTaskAsync(taskToDelete.id); }}
                title="Delete Task"
                description={
                    taskToDelete
                        ? `Are you sure you want to delete "${taskToDelete.title}"? This action cannot be undone.`
                        : ""
                }
            />
        </Card>
    );
}
